import { initApiService } from '@/services/init'
import {
  adminRealmAuthority,
  appRealmAuthority,
  authProvider,
  currentAuthProviderKind,
  rememberedKeycloakAppEmail,
  selectAuthProvider,
  type AuthProviderKind,
} from '@/services/currentAuthProvider'
import { REDIRECT_IN_PROGRESS, type SsoIdentity } from '@/services/AuthProvider'
import { getAppMode, type AppMode } from '@/utils/domain'
import { logUnexpectedFailure } from '@/utils/errorHandling'
import type { SignInMethodRequest } from '@/api/generated-platform'

export type SignInAudience = SignInMethodRequest['audience']

const KEYCLOAK_SIGN_IN: Record<SignInAudience, { kind: AuthProviderKind; authority: () => string | null }> = {
  APP: { kind: 'keycloak-app', authority: appRealmAuthority },
  ADMIN: { kind: 'keycloak-admin', authority: adminRealmAuthority },
}

/** The cliënt portal is not on Keycloak and offers no SSO, so it has no audience here. */
const SSO_AUDIENCE: Partial<Record<AppMode, SignInAudience>> = { behandelaar: 'APP', admin: 'ADMIN' }

/** An outage of the lookup answers `false`: it leaves the person on Firebase. */
async function signsInOnKeycloak(email: string, audience: SignInAudience): Promise<boolean> {
  if (!KEYCLOAK_SIGN_IN[audience].authority()) return false
  try {
    const { data } = await initApiService().signIn.resolveSignInMethod({ email, audience })
    return data.provider === 'KEYCLOAK'
  } catch (error) {
    logUnexpectedFailure('Failed to resolve the sign-in method:', error)
    return false
  }
}

async function startKeycloakSignIn(
  audience: SignInAudience,
  email: string,
  start: () => Promise<unknown>
): Promise<void> {
  selectAuthProvider(KEYCLOAK_SIGN_IN[audience].kind, email)
  try {
    await start()
  } catch (error) {
    if (!(error instanceof Error && error.message === REDIRECT_IN_PROGRESS)) {
      selectAuthProvider('firebase')
      throw error
    }
  }
}

/**
 * Identifier-first sign-in (ADR-045 §7): an e-mailadres decides whether its owner signs in here, with
 * Firebase, or on Keycloak's own page.
 */
export function useKeycloakSignIn() {
  /** Sends the owner of a typed [email] to Keycloak when they sign in there, resolving `true` once under way. */
  async function redirectToKeycloakIfSwitched(email: string, audience: SignInAudience): Promise<boolean> {
    if (!email || !(await signsInOnKeycloak(email, audience))) return false
    await startKeycloakSignIn(audience, email, () => authProvider.signIn(email, ''))
    return true
  }

  /**
   * Decides an SSO sign-in once Firebase has heard from the identity provider, whose answer outranks
   * anything typed on the page. A person on Keycloak leaves through the same identity provider and
   * with no Firebase session behind them, resolving `true` once under way. Runs before any MFA
   * challenge is shown, so they are never asked for their Firebase code.
   */
  async function redirectSsoIdentityToKeycloakIfSwitched({ email, identityProvider }: SsoIdentity): Promise<boolean> {
    const audience = SSO_AUDIENCE[getAppMode()]
    if (!audience || currentAuthProviderKind() !== 'firebase') return false
    if (!email) {
      logUnexpectedFailure('An SSO sign-in named no e-mail address:', new Error('SSO identity without an e-mail'))
      return false
    }
    if (!(await signsInOnKeycloak(email, audience))) return false
    await authProvider.signOut()
    await startKeycloakSignIn(audience, email, () =>
      identityProvider ? authProvider.signInWithSSO(identityProvider, email) : authProvider.signIn(email, '')
    )
    return true
  }

  /**
   * Sends a browser whose last sign-in to the app went through Keycloak back there, resolving `true`
   * once the redirect is under way. Keycloak's own session usually answers without asking anything,
   * so a new tab signs in the way a Firebase one restores its session. The choice is checked with
   * the backend first, because it may predate the organisation switching back; sign-out forgets the
   * address, which also stops this page racing the sign-out's own redirect. `false` leaves the
   * sign-in page as it is, and on Firebase from here on.
   */
  async function resumeKeycloakSignIn(): Promise<boolean> {
    if (currentAuthProviderKind() !== 'keycloak-app') return false
    const email = rememberedKeycloakAppEmail()
    if (!email || !(await signsInOnKeycloak(email, 'APP'))) {
      selectAuthProvider('firebase')
      return false
    }
    try {
      await authProvider.signIn(email, '')
    } catch (error) {
      if (error instanceof Error && error.message === REDIRECT_IN_PROGRESS) return true
      logUnexpectedFailure('Failed to resume the Keycloak sign-in:', error)
      selectAuthProvider('firebase')
      return false
    }
    return true
  }

  return { redirectToKeycloakIfSwitched, redirectSsoIdentityToKeycloakIfSwitched, resumeKeycloakSignIn }
}
